// Unità di misura canoniche lato CLIENT (magazzino/carico/ricette).
//
// ⚠️ TENERE ALLINEATO con `supabase/functions/_shared/uom.ts` (CANONICAL_UOMS):
// quello è la fonte di verità server-side (canonicalizzazione import + conversioni).
// Qui replichiamo solo l'insieme + le etichette per i menu a tendina del client
// (stesso pattern del duplicato allergeni shared↔_shared documentato in CLAUDE.md).
//
// Perché un set fisso: la giacenza tiene UNA sola UdM per alimento e somma le
// quantità per lotto; UdM libere e incoerenti (NR/CAD/buste/colli…) rendono i
// totali di magazzino incomparabili e rompono lo scarico delle ricette (T-290).

import { normalizeComparableUom } from '@trackeat/shared';

export const CANONICAL_UOMS = [
  'pz', 'kg', 'g', 'l', 'ml', 'conf', 'ct', 'cassa', 'bottiglia',
] as const;

export type CanonicalUom = (typeof CANONICAL_UOMS)[number];

// Etichetta breve per i menu a tendina: "<sigla> — <nome esteso>".
export const UOM_LABELS: Record<CanonicalUom, string> = {
  pz: 'pz — pezzi',
  kg: 'kg — chilogrammi',
  g: 'g — grammi',
  l: 'l — litri',
  ml: 'ml — millilitri',
  conf: 'conf — confezioni',
  ct: 'ct — cartoni',
  cassa: 'cassa — casse',
  bottiglia: 'bottiglia — bottiglie',
};

export function uomLabel(u: string): string {
  return (UOM_LABELS as Record<string, string>)[u] ?? u;
}

/** Normalizzazione UdM allineata a Edge/SQL.
 *
 * Gli alias noti diventano canonici (`lt`→`l`, `gr`→`g`, `cad`→`pz`).
 * Un'UdM personalizzata non diventa mai implicitamente `pz`: viene soltanto
 * normalizzata per case/spazi e resta distinta dalle altre unità.
 */
export function normalizeUom(u: string | null | undefined): string {
  return normalizeComparableUom(u);
}

/**
 * Conversione automatica della quantità tra unità della STESSA famiglia
 * (massa kg↔g, volume l↔ml) + identità. Ritorna `null` quando la conversione
 * NON è derivabile da sola (es. pz↔kg, ct→kg): in quei casi serve la quantità
 * reale dall'utente. Mirror client di `convertUom` in `functions/_shared/uom.ts`
 * (massa/volume); il pack→pz lì richiede comunque un pack_size noto.
 */
export function convertQty(qty: number, from: string, to: string): number | null {
  const f = normalizeUom(from);
  const t = normalizeUom(to);
  if (f === t) return qty;
  if (f === 'g' && t === 'kg') return qty / 1000;
  if (f === 'kg' && t === 'g') return qty * 1000;
  if (f === 'ml' && t === 'l') return qty / 1000;
  if (f === 'l' && t === 'ml') return qty * 1000;
  return null;
}

export function isCanonicalUom(u: string | null | undefined): u is CanonicalUom {
  return !!u && (CANONICAL_UOMS as readonly string[]).includes(u);
}

// Unità "base" (contenuto reale tracciabile in magazzino e scaricabile dalle
// ricette). Tutto il resto (conf/ct/cassa/bottiglia + sigle libere collo) è un
// "collo/imballo" e, se la quantità è il numero di colli, va convertito al
// contenuto via fattore (T-294B).
const BASE_UOMS = new Set<string>(['kg', 'g', 'l', 'ml', 'pz']);

export function isBaseUom(u: string | null | undefined): boolean {
  return !!u && BASE_UOMS.has(normalizeUom(u));
}

// T-316: opzioni mostrate nel selettore "Unità base" del carico. Richiesta
// LeBoccerie: la lista delle UdM BASE deve essere solo Kg / PZ / Lt — le famiglie
// reali del magazzino (massa→kg, conteggio→pz, volume→l). g/ml restano UdM
// valide in INPUT (vengono convertite a kg/l da convertQty/suggestPackFactor),
// ma NON sono scelte-base offerte all'utente: la giacenza si tiene in kg/pz/l.
export const BASE_UOM_OPTIONS = ['kg', 'pz', 'l'] as const;

/** True se l'unità è un imballo/collo (non un'unità base) → può servire il fattore. */
export function isPackUnit(u: string | null | undefined): boolean {
  if (!u || !u.trim()) return false;
  return !isBaseUom(u);
}

/** Normalizzazione descrizione allineata alla RPC `upsert_supplier_item_alias`
 *  (lower + collapse whitespace + trim) per il match con `raw_description_norm`. */
export function normalizeAliasDesc(s: string | null | undefined): string {
  return (s ?? '').toLowerCase().trim().replace(/\s+/g, ' ');
}

/**
 * T-294B: prova a dedurre dalla descrizione del documento il CONTENUTO per
 * collo (fattore + unità base), per PRE-COMPILARE l'alert del fattore la prima
 * volta. Pattern reali osservati (validati sulle foto dei DDT):
 *   "SG KG-3", "KG-2,5", "KG.2.5"         → 3 / 2,5 kg per collo
 *   "LT 5", "LT.5"                          → 5 l per collo
 *   "12PZ*CT", "PZ.50*8CF", "Pz 8"         → 12 / 50 / 8 pz per collo
 *   "2 X 250GR", "6X1L", "3 X 1-KG"        → 0,5 kg / 6 l / 3 kg per collo
 * Ritorna null se non deducibile (l'utente inserisce a mano).
 */
export function suggestPackFactor(
  description: string | null | undefined,
  targetUom?: string | null,
): { factor: number; baseUom: CanonicalUom } | null {
  const d = (description ?? '').toLowerCase();
  if (!d) return null;
  const num = (s: string): number => Number(s.replace(',', '.'));
  // Converte (valore, unità) → (quantità in unità base, unità base).
  const toBase = (q: number, unit: string): { q: number; u: CanonicalUom } => {
    if (unit === 'g' || unit === 'gr' || unit === 'grammi') return { q: q / 1000, u: 'kg' };
    if (unit === 'ml') return { q: q / 1000, u: 'l' };
    if (unit === 'cl') return { q: q / 100, u: 'l' };
    if (unit === 'lt' || unit === 'l') return { q, u: 'l' };
    return { q, u: 'kg' }; // kg
  };
  const UNIT = '(kg|gr|grammi|g|ml|cl|lt|l)';

  // "N x M unità" (forward: es. 2 x 250gr, 6x1l, 3 x 1-kg) → N×M.
  // fix (R3 #1): l'unità deve essere CONTIGUA al secondo numero (solo "-" opzionale,
  // NIENTE spazio) — altrimenti "9x9 KG-2,5" (taglio 9×9 + contenuto 2,5 kg) veniva
  // letto come "9 colli × 9 kg = 81". Con la contiguità "9x9 KG-2,5" non matcha qui
  // e cade sul ramo single ("KG-2,5" → 2,5 kg). I multipack veri restano: "250gr",
  // "1l", "1-kg" hanno l'unità attaccata a M.
  const fwd = d.match(new RegExp(`(\\d+[.,]?\\d*)\\s*[x×]\\s*(\\d+[.,]?\\d*)-?${UNIT}\\b`));
  if (fwd) {
    // difesa extra: "AxA" interi uguali (9x9, 13x13, 7x7) = dimensione di taglio,
    // mai un pack → non trattarlo come moltiplicatore.
    const a1 = num(fwd[1]); const a2 = num(fwd[2]);
    const isCutDim = Number.isInteger(a1) && a1 === a2 && a1 <= 50;
    if (!isCutDim) {
      const r = toBase(a2, fwd[3]);
      const f = a1 * r.q;
      if (f > 0) return normalizeUom(targetUom ?? '') === 'pz'
        ? { factor: a1, baseUom: 'pz' }
        : { factor: Number(f.toFixed(4)), baseUom: r.u };
    }
  }
  // "M unità x N" (fix #3, inverse: es. 125 G x 3, 33 CL x 24) → M×N.
  const inv = d.match(new RegExp(`(\\d+[.,]?\\d*)\\s*-?\\s*${UNIT}\\b\\s*[x×]\\s*(\\d+[.,]?\\d*)`));
  if (inv) {
    const r = toBase(num(inv[1]), inv[2]);
    const f = r.q * num(inv[3]);
    if (f > 0) return normalizeUom(targetUom ?? '') === 'pz'
      ? { factor: num(inv[3]), baseUom: 'pz' }
      : { factor: Number(f.toFixed(4)), baseUom: r.u };
  }

  // "CL33X24 VAP" / "CL 33X24VAP": il 330 ml strutturato dall’IA
  // descrive una bottiglia, non l’intera confezione di 24 pezzi.
  const prefixed = d.match(/\b(kg|gr|ml|cl|lt)\s*[-.]?\s*(\d+(?:[.,]\d+)?)\s*[x×]\s*(\d+)(?!\d)/);
  if (prefixed) {
    const count = num(prefixed[3]);
    const r = toBase(num(prefixed[2]), prefixed[1]);
    if (count > 0 && r.q > 0) return normalizeUom(targetUom ?? '') === 'pz'
      ? { factor: count, baseUom: 'pz' }
      : { factor: Number((count * r.q).toFixed(4)), baseUom: r.u };
  }

  // peso/volume singolo per collo: "KG-3", "kg.2,5", "lt 5", "gr 200", "200 g".
  // fix #4: niente falsi positivi su "lettera-unità + numero" tipo "L 500" /
  // "G 1000": le unità a singola lettera valgono SOLO se attaccate al numero con
  // - o . (i codici reali sono "KG-3", "kg.2,5"); le multi-lettera con spazio ok.
  const single = d.match(/\b(kg|gr|lt|ml|cl)\b\s*[-.\s]?\s*(\d+[.,]?\d*)/)
    || d.match(/\b(g|l)[-.](\d+[.,]?\d*)/)
    // numero + unità: multi-lettera con spazio ok ("gr 200", "200 ml")…
    || d.match(/(\d+[.,]?\d*)\s*(kg|gr|grammi|lt|ml|cl)\b/)
    // …unità a singola lettera (g|l) SOLO contigua (R5 #2: evita falsi positivi
    // su numeri lunghi/codici tipo "LOTTO 2026 L" → 2026 l).
    || d.match(/(\d+[.,]?\d*)(g|l)\b/);
  if (single) {
    const leadUnit = /^[a-z]/.test(single[1]);
    const unit = leadUnit ? single[1] : single[2];
    const val = num(leadUnit ? single[2] : single[1]);
    if (val > 0) {
      const r = toBase(val, unit);
      return { factor: Number(r.q.toFixed(4)), baseUom: r.u };
    }
  }

  // pezzi per collo: "12pz*ct", "pz.50", "pz 8", "50 pz".
  const pz = d.match(/pz[.\s]*(\d+)/) || d.match(/(\d+)\s*pz\b/);
  if (pz) {
    const v = Number(pz[1]);
    if (v > 0) return { factor: v, baseUom: 'pz' };
  }
  return null;
}

// ── UdM personalizzate ricordate sul dispositivo ──
// L'utente può digitare un'UdM non in lista; viene ricordata e poi proposta tra
// le scelte (richiesta utente: "una riga libera che al momento dell'inserimento
// va a popolare le scelte fisse e suggerite"). Solo locale (preferenza UI), non
// tocca lo schema dati.
const CUSTOM_UOMS_KEY = 'trackeat.custom_uoms';

export function getCustomUoms(): string[] {
  try {
    const raw = localStorage.getItem(CUSTOM_UOMS_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw) as unknown;
    if (!Array.isArray(arr)) return [];
    return arr.filter((x): x is string => typeof x === 'string');
  } catch {
    return [];
  }
}

/** Normalizza e memorizza un'UdM personalizzata (no-op se canonica o vuota). */
export function rememberCustomUom(raw: string): void {
  const u = raw.trim();
  if (!u || isCanonicalUom(u)) return;
  try {
    const cur = getCustomUoms();
    if (cur.some((x) => x.toLowerCase() === u.toLowerCase())) return;
    localStorage.setItem(CUSTOM_UOMS_KEY, JSON.stringify([...cur, u].slice(-30)));
  } catch {
    // localStorage non disponibile: ignoriamo, il valore resta comunque usato.
  }
}

/**
 * Opzioni da mostrare nel selettore: canoniche + personalizzate ricordate +
 * l'eventuale valore corrente fuori-lista (così un'UdM estratta dall'AI non in
 * set resta visibile/selezionata invece di sparire).
 */
export function uomOptions(current?: string | null): string[] {
  const out: string[] = [...CANONICAL_UOMS];
  for (const c of getCustomUoms()) {
    if (!out.some((x) => x.toLowerCase() === c.toLowerCase())) out.push(c);
  }
  if (current && !out.some((x) => x.toLowerCase() === current.toLowerCase())) {
    out.push(current);
  }
  return out;
}
